import { debounce } from "throttle-debounce";

import { Point } from "../../models/point";

export class CameraContext {
	public constructor(
		public position: Point = new Point(0, 0),
		public target: Point = new Point(0, 0),
		public keepCameraInitialPosition: boolean | undefined = false
	) {}

	public get positionChanged(): boolean {
		return this.position.x + this.position.y + this.position.z > 0;
	}
}

export interface ICameraRotation {
	quaternion: THREE.Quaternion;
}

export interface ICameraExtensionOptions {
	onDebounceCameraChanged?: (rotation?: ICameraRotation) => void;
	cameraContext: CameraContext;
	keepCameraInitialPosition?: boolean;
}

export class CameraExtension extends Autodesk.Viewing.Extension {
	private cameraContext: CameraContext;
	public options: ICameraExtensionOptions;

	public constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: ICameraExtensionOptions) {
		super(viewer, options);

		this.options = options;
		this.cameraContext = options.cameraContext;
		this.onCameraChanged = this.onCameraChanged.bind(this);
		this.onModelLayersLoadedEvent = this.onModelLayersLoadedEvent.bind(this);
	}

	public load(): boolean {
		this.viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChanged);
		this.viewer.addEventListener(Autodesk.Viewing.MODEL_LAYERS_LOADED_EVENT, this.onModelLayersLoadedEvent);
		return true;
	}

	public unload(): boolean {
		this.debounceOnCameraChanged.cancel();

		this.viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.onCameraChanged);
		this.viewer.removeEventListener(Autodesk.Viewing.MODEL_LAYERS_LOADED_EVENT, this.onModelLayersLoadedEvent);
		return true;
	}

	private onModelLayersLoadedEvent(): void {
		if (this.cameraContext.positionChanged) {
			this.viewer.navigation.setView(Point.ToVector(this.cameraContext.position), Point.ToVector(this.cameraContext.target));
		}
	}

	private onCameraChanged(cameraChangedEvent: any): void {
		this.saveCameraPosition(cameraChangedEvent.camera);
		this.debounceOnCameraChanged(cameraChangedEvent.camera);
	}

	private debounceOnCameraChanged = debounce(100, (camera: any) => {
		if (this.options.onDebounceCameraChanged) {
			this.options.onDebounceCameraChanged({
				quaternion: camera.quaternion,
			});
		}
	});

	private saveCameraPosition(camera: CameraContext): void {
		if (this.cameraContext.keepCameraInitialPosition) {
			return;
		}

		this.cameraContext.position = { ...camera.position };
		this.cameraContext.target = { ...camera.target };
		this.cameraContext.keepCameraInitialPosition = this.options.keepCameraInitialPosition;
	}
}
